/**
 * UserRoleSelect
 *
 * Shared bare `<Select>` for choosing an active org user eligible for
 * loan-officer roles (loan_officer / admin / manager). Renders no label — the
 * caller wraps it in an `<LwField label error>` grid cell. Factored out of the
 * former near-identical MentorSelect + OverrideRecipientSelect.
 *
 * Preserves the `none` ↔ `''` mapping, the User-icon display, and the
 * loading/fetch-error states of the originals.
 */

import { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Loader2, User } from 'lucide-react';
import { supabase } from '@/utils/supabase';

interface LoanOfficer {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
}

interface UserRoleSelectProps {
  id?: string;
  value?: string;
  onChange: (userId: string) => void;
  error?: string;
  placeholder?: string;
}

export function UserRoleSelect({
  id,
  value,
  onChange,
  error,
  placeholder = 'Select…'
}: UserRoleSelectProps) {
  const [officers, setOfficers] = useState<LoanOfficer[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    fetchLoanOfficers();
  }, []);

  const fetchLoanOfficers = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      // Get the current user's organization
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      const { data: currentProfile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!currentProfile) {
        throw new Error('User profile not found');
      }

      // Fetch all active users in the organization
      const { data, error: queryError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, full_name, roles')
        .eq('organization_id', currentProfile.organization_id)
        .eq('employment_status', 'active')
        .order('full_name');

      if (queryError) {
        throw queryError;
      }

      // Filter to only loan officers, admins, and managers (eligible)
      const filteredData = data?.filter(u =>
        u.roles && (
          u.roles.includes('loan_officer') ||
          u.roles.includes('admin') ||
          u.roles.includes('manager')
        )
      );

      setOfficers(filteredData || []);
    } catch (err) {
      console.error('Error fetching loan officers:', err);
      setFetchError(err instanceof Error ? err.message : 'Failed to load loan officers');
    } finally {
      setLoading(false);
    }
  };

  const getOfficerDisplayName = (officer: LoanOfficer) => {
    if (officer.full_name) {
      return officer.full_name;
    }
    if (officer.first_name && officer.last_name) {
      return `${officer.first_name} ${officer.last_name}`;
    }
    if (officer.first_name) {
      return officer.first_name;
    }
    if (officer.last_name) {
      return officer.last_name;
    }
    return officer.email;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, border: '1px solid var(--line)', borderRadius: 9, background: 'var(--lw-card)' }}>
        <Loader2 className="animate-spin" size={16} style={{ color: 'var(--lw-muted)' }} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--danger)', fontWeight: 600 }}>
        Error loading loan officers: {fetchError}
      </div>
    );
  }

  const selected = value ? officers.find((o) => o.id === value) : undefined;

  return (
    <Select
      value={value || 'none'}
      onValueChange={(newValue) => {
        onChange(newValue === 'none' ? '' : newValue);
      }}
    >
      <SelectTrigger id={id} style={error ? { borderColor: 'var(--danger)' } : undefined}>
        <SelectValue placeholder={placeholder}>
          {selected ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <User size={15} />
              {getOfficerDisplayName(selected)}
            </span>
          ) : (
            placeholder
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="lw-ds">
        <SelectItem value="none">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <User size={15} style={{ opacity: 0.5 }} />
            None
          </span>
        </SelectItem>
        {officers.map((officer) => (
          <SelectItem key={officer.id} value={officer.id}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <User size={15} />
              {getOfficerDisplayName(officer)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
